import { ref, onUnmounted } from 'vue'
import store from '@/store'

export const RECONNECT_INTERVAL = 5000
export const MAX_RECONNECT_ATTEMPTS = 10

class WebSocketService {
  constructor() {
    this.ws = null
    this.reconnectAttempts = 0
    this.reconnectTimeout = null
    this.isConnecting = false
    this.isConnected = ref(false)
    this.connectionError = ref(null)
    this.listeners = new Map()
    this.messageQueue = []
    this.apiDomain = null
  }

  getWebSocketUrl() {
    // Use environment variables for WebSocket configuration
    const host = import.meta.env.VITE_REVERB_HOST || 'newsdesk-api.scrippsnet.io'
    const port = import.meta.env.VITE_REVERB_PORT || '443'
    const scheme = import.meta.env.VITE_REVERB_SCHEME || 'wss'
    const basePath = import.meta.env.VITE_REVERB_PATH || '/wss'
    const appKey = import.meta.env.VITE_REVERB_APP_KEY || 'newsdesk-key'

    // Pusher protocol parameters expected by Laravel Reverb
    const protocol = '7'
    const client = 'js'
    const version = '7.0.0'

    const pathWithApp = `${basePath}/app/${encodeURIComponent(appKey)}?protocol=${protocol}&client=${client}&version=${encodeURIComponent(version)}`

    // Construct WebSocket URL - using /wss path through main HTTPS port
    if (String(port) === '443' && scheme === 'wss') {
      return `${scheme}://${host}${pathWithApp}`
    }
    return `${scheme}://${host}:${port}${pathWithApp}`
  }

  connect() {
    if (this.isConnecting || this.ws?.readyState === WebSocket.OPEN) {
      return
    }

    this.isConnecting = true
    this.connectionError.value = null

    try {
      const wsUrl = this.getWebSocketUrl()
      console.log('[WebSocket] Connecting to:', wsUrl, 'Origin:', window.location.origin)

      this.ws = new WebSocket(wsUrl)

      this.ws.onopen = () => {
        console.log('[WebSocket] Connected successfully')
        this.isConnected.value = true
        this.isConnecting = false
        this.reconnectAttempts = 0
        this.connectionError.value = null

        // Process any queued messages
        this.processMessageQueue()

        // Always subscribe to server-info for demo
        this.subscribe('server-info')

        // Subscribe to station-specific channels if we have a station
        const currentStation = store.getters.currentStation
        if (currentStation?.station) {
          this.subscribeToStation(currentStation.station)
        }
      }

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data)
          // Normalize payload: data may be a JSON string
          if (typeof message.data === 'string') {
            try {
              message.data = JSON.parse(message.data)
            } catch (_) {}
          }
          console.log('[WebSocket] Message received:', message)

          this.handleMessage(message)
        } catch (error) {
          console.error('[WebSocket] Error parsing message:', error)
        }
      }

      this.ws.onerror = (error) => {
        // The browser's error event carries no detail and onclose reconnects, so this is
        // a warning: RUM records console errors and these only added noise.
        console.warn('[WebSocket] Error:', error)
        this.connectionError.value = 'WebSocket connection error'
        this.isConnecting = false
      }

      this.ws.onclose = (event) => {
        console.log('[WebSocket] Connection closed:', event.code, event.reason)
        this.isConnected.value = false
        this.isConnecting = false
        this.ws = null

        // Attempt to reconnect if not a normal closure
        if (event.code !== 1000 && this.reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
          this.scheduleReconnect()
        }
      }
    } catch (error) {
      console.error('[WebSocket] Connection error:', error)
      this.connectionError.value = error.message
      this.isConnecting = false
      this.scheduleReconnect()
    }
  }

  scheduleReconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
    }

    this.reconnectAttempts++
    const delay = Math.min(RECONNECT_INTERVAL * this.reconnectAttempts, 30000)

    console.log(`[WebSocket] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`)

    this.reconnectTimeout = setTimeout(() => {
      this.connect()
    }, delay)
  }

  disconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }

    if (this.ws) {
      this.ws.close(1000, 'Client disconnect')
      this.ws = null
    }

    this.isConnected.value = false
    this.isConnecting = false
    this.reconnectAttempts = 0
    this.messageQueue = []
  }

  subscribeToStation(station) {
    // Subscribe to station-specific channels
    this.subscribe(`station.${station}`)
    this.subscribe(`tasks.${station}`)
    this.subscribe(`media.${station}`)
    this.subscribe(`scripts.${station}`)
    // Subscribe to server-info channel for demo
    this.subscribe('server-info')
  }

  subscribe(channel) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('[WebSocket] Cannot subscribe - not connected')
      return
    }

    const message = {
      event: 'pusher:subscribe',
      data: {
        channel: channel
      }
    }

    this.send(message)
  }

  unsubscribe(channel) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return
    }

    const message = {
      event: 'pusher:unsubscribe',
      data: {
        channel: channel
      }
    }

    this.send(message)
  }

  send(data) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data))
    } else {
      // Queue message for later
      this.messageQueue.push(data)
    }
  }

  processMessageQueue() {
    while (this.messageQueue.length > 0 && this.ws?.readyState === WebSocket.OPEN) {
      const message = this.messageQueue.shift()
      this.send(message)
    }
  }

  handleMessage(data) {
    // Handle Pusher protocol messages
    if (data.event === 'pusher:connection_established') {
      console.log('[WebSocket] Connection established:', data.data)
      return
    }

    if (data.event === 'pusher:error') {
      console.error('[WebSocket] Pusher error:', data.data)
      this.connectionError.value = data.data.message || 'WebSocket error'
      return
    }

    // Respond to server pings
    if (data.event === 'pusher:ping') {
      this.send({ event: 'pusher:pong' })
      return
    }

    // Handle subscription confirmations
    if (data.event === 'pusher_internal:subscription_succeeded') {
      console.log('[WebSocket] Subscribed to channel:', data.channel)
      return
    }

    // Route messages to appropriate handlers
    if (data.channel && data.event) {
      this.notifyListeners(data.channel, data.event, data.data)
    }
  }

  on(channel, event, callback) {
    const key = `${channel}:${event}`
    if (!this.listeners.has(key)) {
      this.listeners.set(key, new Set())
    }
    this.listeners.get(key).add(callback)

    // Return cleanup function
    return () => {
      const listeners = this.listeners.get(key)
      if (listeners) {
        listeners.delete(callback)
        if (listeners.size === 0) {
          this.listeners.delete(key)
        }
      }
    }
  }

  notifyListeners(channel, event, data) {
    const key = `${channel}:${event}`
    const listeners = this.listeners.get(key)
    if (listeners) {
      listeners.forEach((callback) => {
        try {
          callback(data)
        } catch (error) {
          console.error('[WebSocket] Error in listener callback:', error)
        }
      })
    }

    // Also check for wildcard listeners
    const wildcardKey = `${channel}:*`
    const wildcardListeners = this.listeners.get(wildcardKey)
    if (wildcardListeners) {
      wildcardListeners.forEach((callback) => {
        try {
          callback({ event, data })
        } catch (error) {
          console.error('[WebSocket] Error in wildcard listener callback:', error)
        }
      })
    }
  }
}

// Create singleton instance
export const websocketService = new WebSocketService()

// Vue composable for WebSocket functionality
export function useWebSocket() {
  const cleanup = []

  const subscribe = (channel, event, callback) => {
    const unsubscribe = websocketService.on(channel, event, callback)
    cleanup.push(unsubscribe)
    return unsubscribe
  }

  // Clean up on component unmount
  onUnmounted(() => {
    cleanup.forEach((fn) => fn())
  })

  return {
    isConnected: websocketService.isConnected,
    connectionError: websocketService.connectionError,
    subscribe,
    send: (data) => websocketService.send(data),
    connect: () => websocketService.connect(),
    disconnect: () => websocketService.disconnect()
  }
}
